import React, { Component } from 'react';
import {
  injectIntl, defineMessages,
} from 'react-intl';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Panel } from 'primereact/panel';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Message } from 'primereact/message';
import * as Yup from 'yup';
import Debug from '../../../components/Debug';
import { growlShape, intlShape } from '../../../components/prop-types';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlWarning, growlErrors } from '../../../utils/NotificationUtil';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import { getAccountsToTransferToInOrganisation } from '../../../services/apps/AccountService';

const getPreSelected = (transferToOptions) => {
  if (!transferToOptions.size > 0) {
    return null;
  }

  return transferToOptions[0].id;
};

const i18n = defineMessages({
  transferFrom: { id: 'menu.transfer_from', defaultMessage: 'From: ' },
  transferTo: { id: 'menu.transfer_to', defaultMessage: 'To: ' },
  transferSubmit: { id: 'menu.transfer_submit', defaultMessage: 'Transfer' },
  transferAmount: { id: 'menu.transfer_amount', defaultMessage: 'Amount' },
  transferReason: { id: 'menu.transfer_reason', defaultMessage: 'Reason' },
  transferReasonHint: { id: 'menu.transfer_reason_hint', defaultMessage: 'Please enter a reason for transfer.' },
  transferAmountHint: {
    id: 'menu.transfer_amount_hint',
    defaultMessage: 'Please enter a valid value greater than zero.',
  },
  creditThresholdReached: {
    id: 'credit-threshold-reached',
    defaultMessage: 'The transfer was made but the credit notification threshold for this account was reached',
  },
});

class TransferForm extends Component {
  constructor() {
    super();

    this.state = {
      accounts: [],
      accountsLoading: false,
    };

    this.createTransfer = this.createTransfer.bind(this);
    this.handleSearchAccounts = this.handleSearchAccounts.bind(this);
  }

  componentDidMount() {
    this.handleSearchAccounts();
  }

  async createTransfer(values, resetForm) {
    const {
      growl, transferCredits, history, account,
      intl: { formatMessage },
    } = this.props;

    const { id } = account;

    try {
      const result = await transferCredits({
        variables: {
          input: {
            creditorId: values.creditorId,
            debtorId: values.debtor.id,
            amount: values.amount,
            reason: values.reason,
            isDemo: values.isDemo,
          },
        },
      });

      const { creditThresholdReached } = result.data.transferCredits;

      if (creditThresholdReached) {
        growlWarning(growl, formatMessage(i18n.creditThresholdReached));
        resetForm();
      } else {
        history.push(`/accounts/${id}/organisation-transfers`);
      }
    } catch (err) {
      resetForm();
      growlErrors(growl, err);
    }
  }

  async handleSearchAccounts(query = null) {
    const { growl, account } = this.props;

    const { id } = account;

    try {
      this.setState({ accountsLoading: true });
      const data = await getAccountsToTransferToInOrganisation({
        id,
        query,
      });
      this.setState({ accounts: data, accountsLoading: false });
    } catch (err) {
      this.setState({ accountsLoading: false });
      growlErrors(growl, err);
    }
  }

  render() {
    const {
      account,
      intl: { formatMessage },
    } = this.props;

    const { id, displayName } = account;

    const transferFromOptions = [
      {
        label: displayName,
        value: id,
      },
    ];

    const { accounts, accountsLoading } = this.state;

    const transferToOptions = accounts
      .filter(a => a.id !== id)
      .map(a => ({
        name: `${a.displayName} - ${a.id}`,
        id: a.id,
      }));

    const preSelected = getPreSelected(transferToOptions);

    const initialValues = {
      creditorId: id,
      debtor: preSelected,
      amount: 1,
      isDemo: false,
    };

    const validationSchema = Yup.object().shape({
      isDemo: Yup.bool().isRequired,
      reason: Yup.string().required(formatMessage(i18n.transferReasonHint)),
      amount: Yup.number().required(formatMessage(i18n.transferAmountHint)),
    });

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel>
              <div className="p-grid">
                <div className="p-sm-12 p-md-6">
                  <Formik
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={(values, formikBag) => {
                      const { resetForm } = formikBag;
                      this.createTransfer(values, resetForm);
                    }}
                  >
                    {({
                      isSubmitting, handleChange, values, setFieldValue, errors,
                    }) => (
                      <Form>
                        <div className="p-grid p-fluid">
                          <input type="hidden" name="isDemo" value={false} />
                          <div className="p-col-12">
                            <label>{formatMessage(i18n.transferFrom)}</label>
                            <Field
                              component={Dropdown}
                              id="creditorId"
                              name="creditorId"
                              key="creditorId"
                              value={values.creditorId}
                              options={transferFromOptions}
                              onChange={handleChange}
                            />
                          </div>

                          <div className="p-col-12">
                            <label>{formatMessage(i18n.transferTo)}</label>
                            <Field
                              id="debtor"
                              name="debtor"
                              key="debtor"
                              required
                              textField="name"
                              valueField="id"
                              filter
                              component={renderAsyncSelector}
                              processing={accountsLoading}
                              data={transferToOptions}
                              onChange={handleChange}
                              setFieldValue={setFieldValue}
                              onSearch={this.handleSearchAccounts}
                            />
                          </div>

                          <div className="p-col-12">
                            <label>{formatMessage(i18n.transferAmount)}</label>
                            <Field
                              component={InputText}
                              id="amount"
                              name="amount"
                              type="number"
                              value={values.amount}
                              min="1"
                              step="1"
                              onChange={handleChange}
                            />
                            {errors.amount
                              && <Message severity="error" text={formatMessage(i18n.transferAmountHint)} />}
                          </div>

                          <div className="p-col-12">
                            <label>{formatMessage(i18n.transferReason)}</label>
                            <Field
                              component={InputTextarea}
                              id="reason"
                              name="reason"
                              value={values.reason}
                              onChange={handleChange}
                            />
                            {errors.reason
                              && <Message severity="error" text={formatMessage(i18n.transferReasonHint)} />}
                          </div>

                        </div>
                        <div className="p-grid">
                          <div className="p-col-12">
                            <Button
                              label={formatMessage(i18n.transferSubmit)}
                              type="submit"
                              disabled={isSubmitting}
                            />
                            <Debug />
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
}

const accountShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  creditBalance: PropTypes.objectOf(PropTypes.any).isRequired,
});

TransferForm.propTypes = {
  account: accountShape.isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  transferCredits: PropTypes.func.isRequired,
  mutationResult: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
};

export default withGrowl(injectIntl(withRouter(TransferForm)));
